// Admin "Fiche client" — single-customer PROFILE / detail page.
//
// Land here from the Clients list (/admin/customers/:id). Shows everything
// about one person: identity, lifetime money + profitability (read straight
// from the API — never recomputed), full order history (newest first) with
// AMEEX delivery state, a deferred coupons placeholder, and an editable notes
// box. Built to be dead-simple for a brand-new team member.
//
// Premium cream/gold styling matching AdminCustomersPage + AdminAccountingPage:
// KPI cards, large Fraunces money numbers, sage positive / terracotta negative,
// rounded-xl cards with subtle shadow, generous whitespace, ≥44px touch
// targets, mobile-first.

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  customersApi,
  type CustomerDetail,
  type CustomerOrderHistory,
} from '@/lib/api';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import { ADM_COLORS as COLORS } from '@/lib/brand';
import { useT, type TFunc } from '@/lib/i18n';

const HEADING = "'Fraunces', 'Cormorant Garamond', Georgia, serif";
const cardStyle = {
  background: COLORS.bgCard,
  borderColor: COLORS.borderSubtle,
  boxShadow: '0 1px 3px rgba(46,36,24,0.04), 0 8px 24px rgba(46,36,24,0.05)',
} as const;

const mad = (n: number) =>
  `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(n || 0))} MAD`;
const moneyColor = (n: number) =>
  n > 0 ? COLORS.success : n < 0 ? COLORS.error : COLORS.textSecondary;

// ─── AMEEX status → French label + brand color ───────────────────────────────
// Matches the conventions in components/admin/AmeexDeliveryCard.tsx (sage =
// delivered, terracotta = returned/refused, amber = in-flight, grey = inert).
// Hue pills carry their colours as classes (light value + dark: status token);
// the neutral ones use the night-aware palette inline.
type AmeexTone = { label: string; bg?: string; text?: string; cls?: string; dot: string };
const WAITING = 'bg-[#FBF1E2] text-[#8A5A24] dark:bg-status-waiting-bg dark:text-status-waiting';
const DONE = 'bg-[#EEF1EA] text-[#4F5E43] dark:bg-status-done-bg dark:text-status-done';
const STUCK = 'bg-[#F7E7E0] text-[#8A3D24] dark:bg-status-stuck-bg dark:text-status-stuck';
const AMEEX_TONE: Record<string, AmeexTone> = {
  pending:      { label: 'En attente',     bg: COLORS.bgElevated, text: COLORS.textSecondary, dot: COLORS.textTertiary },
  in_progress:  { label: 'En cours',       cls: WAITING,                                       dot: COLORS.warning },
  distribution: { label: 'En distribution',cls: WAITING,                                       dot: COLORS.warning },
  delivered:    { label: 'Livré',          cls: DONE,                                          dot: COLORS.success },
  postponed:    { label: 'Reporté',        cls: WAITING,                                       dot: COLORS.warning },
  returned:     { label: 'Retourné',       cls: STUCK,                                         dot: COLORS.error },
  refused:      { label: 'Refusé',         cls: STUCK,                                         dot: COLORS.error },
  cancelled:    { label: 'Annulé',         bg: COLORS.bgElevated, text: COLORS.textSecondary, dot: COLORS.textTertiary },
  refunded:     { label: 'Remboursé',      bg: COLORS.bgElevated, text: COLORS.textSecondary, dot: COLORS.textTertiary },
  unresolved:   { label: 'Non résolu',     cls: WAITING,                                       dot: COLORS.warning },
};
const NO_TRACKING: AmeexTone = {
  label: 'Pas de suivi',
  bg: COLORS.bgElevated,
  text: COLORS.textTertiary,
  dot: COLORS.textTertiary,
};
function ameexTone(status: string | null): AmeexTone {
  if (!status) return NO_TRACKING;
  return AMEEX_TONE[status] ?? NO_TRACKING;
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function AdminCustomerDetailPage() {
  const { t, lang } = useT();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['customer', id],
    queryFn: () => customersApi.get(id!),
    enabled: !!id,
  });

  const customer = q.data?.customer;
  const orders = q.data?.orders ?? [];

  const fmtDate = (x: string | null | undefined) =>
    x
      ? new Date(x).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—';

  // Notes — local state seeded from the loaded customer; re-seed when the
  // fetched value changes (e.g. after invalidation/refetch).
  const [note, setNote] = useState('');
  useEffect(() => {
    if (customer) setNote(customer.notes ?? '');
  }, [customer?.id, customer?.notes]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveNotes = useMutation({
    mutationFn: () => customersApi.saveNotes(id!, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer', id] });
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  // Orders arrive oldest-first → show newest-first.
  const ordersNewestFirst = useMemo(() => [...orders].reverse(), [orders]);

  return (
    <AdminLayout title={t('Fiche client')} subtitle={customer?.name ?? ''}>
      <div style={{ background: COLORS.bgPrimary }} className="-m-4 sm:-m-6 p-4 sm:p-6 min-h-full">
        {/* Back link */}
        <Link
          to="/admin/customers"
          className="inline-flex items-center gap-1.5 text-sm font-medium mb-5 transition-colors"
          style={{ color: COLORS.textSecondary }}
        >
          <span aria-hidden>←</span> {t('Clients')}
        </Link>

        {/* States */}
        {q.isLoading && (
          <p className="text-sm py-16 text-center" style={{ color: COLORS.textTertiary }}>{t('Chargement…')}</p>
        )}
        {q.isError && (
          <p className="text-sm py-16 text-center" style={{ color: COLORS.error }}>{(q.error as Error).message}</p>
        )}
        {!q.isLoading && !q.isError && !customer && (
          <p className="text-sm py-16 text-center" style={{ color: COLORS.textTertiary }}>{t('Client introuvable')}</p>
        )}

        {customer && (
          <div className="space-y-6">
            <IdentityHeader customer={customer} fmtDate={fmtDate} t={t} />
            <MoneyPanel customer={customer} t={t} />

            {/* Order history */}
            <section>
              <h2
                className="text-[15px] mb-3"
                style={{ fontFamily: HEADING, fontWeight: 500, color: COLORS.textPrimary }}
              >
                {t('Historique des commandes')} <span style={{ color: COLORS.textTertiary }}>({orders.length})</span>
              </h2>
              {ordersNewestFirst.length === 0 ? (
                <div className="rounded-xl border p-8 text-center" style={cardStyle}>
                  <p className="text-sm" style={{ color: COLORS.textTertiary }}>{t('Aucune commande')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {ordersNewestFirst.map((o) => (
                    <OrderCard key={o.shopifyOrderId} order={o} fmtDate={fmtDate} t={t} />
                  ))}
                </div>
              )}
            </section>

            {/* Coupons — deferred (not tracked yet). */}
            <section>
              <h2
                className="text-[15px] mb-3"
                style={{ fontFamily: HEADING, fontWeight: 500, color: COLORS.textPrimary }}
              >
                {t('Coupons utilisés')}
              </h2>
              <div className="rounded-xl border p-8 text-center" style={cardStyle}>
                <p className="text-sm" style={{ color: COLORS.textTertiary }}>{t('Bientôt disponible')}</p>
                <p className="text-[11px] mt-1" style={{ color: COLORS.textTertiary }}>
                  {t('Les coupons seront suivis prochainement.')}
                </p>
              </div>
            </section>

            {/* Notes */}
            <section>
              <h2
                className="text-[15px] mb-3"
                style={{ fontFamily: HEADING, fontWeight: 500, color: COLORS.textPrimary }}
              >
                {t('Notes internes')}
              </h2>
              <div className="rounded-xl border p-5" style={cardStyle}>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={5}
                  placeholder={t('Ajoutez des notes sur ce client (préférences, historique, rappels…)')}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 bg-white resize-y"
                  style={{ borderColor: COLORS.borderSubtle, color: COLORS.textPrimary }}
                />
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-[11px]" style={{ color: COLORS.textTertiary }}>
                    {customer.notesUpdatedAt
                      ? t('Modifié le {date} par {who}', {
                          date: fmtDate(customer.notesUpdatedAt),
                          who: customer.notesUpdatedBy || t('inconnu'),
                        })
                      : t('Aucune note enregistrée')}
                  </p>
                  <Button
                    onClick={() => saveNotes.mutate()}
                    loading={saveNotes.isPending}
                    disabled={saveNotes.isPending || note === (customer.notes ?? '')}
                  >
                    {t('Enregistrer')}
                  </Button>
                </div>
                {saveNotes.isError && (
                  <p className="text-sm mt-2" style={{ color: COLORS.error }}>{(saveNotes.error as Error).message}</p>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// ─── Identity header ─────────────────────────────────────────────────────────
function IdentityHeader({
  customer,
  fmtDate,
  t,
}: {
  customer: CustomerDetail;
  fmtDate: (x: string | null | undefined) => string;
  t: TFunc;
}) {
  const otherVariants = customer.nameVariants.filter((v) => v && v !== customer.name);
  return (
    <div className="rounded-xl border p-5 sm:p-6 adm-rise" style={cardStyle}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1
            className="text-[26px] leading-tight"
            style={{ fontFamily: HEADING, fontWeight: 500, color: COLORS.textPrimary, letterSpacing: '-0.01em' }}
          >
            {customer.name || '—'}
          </h1>
          <p className="text-sm mt-1" style={{ color: COLORS.textSecondary }}>{customer.phone}</p>
          {customer.email && (
            <p className="text-sm" style={{ color: COLORS.textSecondary }}>{customer.email}</p>
          )}
          {otherVariants.length > 0 && (
            <p className="text-[11px] mt-1.5" style={{ color: COLORS.textTertiary }}>
              {t('Aussi connu sous:')} {otherVariants.join(', ')}
            </p>
          )}
        </div>
        {customer.acquisitionSource && (
          <span
            className="text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
            style={{ background: COLORS.bgElevated, color: COLORS.textSecondary }}
          >
            {customer.acquisitionSource}
          </span>
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <Meta label={t('Client depuis')} value={fmtDate(customer.firstOrderAt)} />
        <Meta label={t('Dernière commande')} value={fmtDate(customer.lastOrderAt)} />
        <Meta label={t('Commandes')} value={String(customer.ordersCount)} />
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.textTertiary }}>{label}</p>
      <p className="text-sm font-medium" style={{ color: COLORS.textPrimary }}>{value}</p>
    </div>
  );
}

// ─── Money panel ─────────────────────────────────────────────────────────────
function MoneyPanel({
  customer,
  t,
}: {
  customer: CustomerDetail;
  t: TFunc;
}) {
  const m = customer.money;
  // CAC / LTV graceful handling: ad cost being 0 is normal (ad tokens not set
  // up yet). Only show a real ratio when ltvToCac is non-null AND cac > 0;
  // otherwise show "—" with a muted "ad cost not configured" note.
  const hasCac = m.ltvToCac != null && m.cac > 0;
  const ltvLabel = hasCac ? `${m.ltvToCac!.toFixed(1)}× CAC` : '—';

  return (
    <div className="space-y-4">
      {/* Revenue / AOV / orders */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard label={t('Revenu à vie')} value={mad(m.lifetimeRevenue)} accent />
        <KpiCard label={t('Panier moyen')} value={mad(m.aov)} sub={t('par commande')} />
        <KpiCard label={t('Commandes')} value={String(m.ordersCount)} />
      </div>

      {/* COGS / contribution / ad cost */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard label={t('Coût des marchandises (à vie)')} value={mad(m.lifetimeCogs)} />
        <KpiCard
          label={t('Contribution (à vie)')}
          value={mad(m.lifetimeContribution)}
          valueColor={moneyColor(m.lifetimeContribution)}
        />
        <KpiCard label={t('Coût pub (à vie)')} value={mad(m.lifetimeAdCost)} />
      </div>

      {/* CAC / net profit / LTV:CAC */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard label={t('Coût d’acquisition (1ʳᵉ commande)')} value={mad(m.cac)} />
        <KpiCard
          label={t('Profit net')}
          value={mad(m.netProfit)}
          valueColor={m.netProfit >= 0 ? COLORS.success : COLORS.error}
          accent
        />
        <KpiCard
          label={t('LTV vs CAC')}
          value={ltvLabel}
          valueColor={hasCac && m.ltvToCac! >= 1 ? COLORS.success : COLORS.textPrimary}
          sub={hasCac ? undefined : t('Coût pub non configuré')}
        />
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  accent = false,
  valueColor = COLORS.textPrimary,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  valueColor?: string;
}) {
  return (
    <div className="rounded-xl p-5 border relative overflow-hidden adm-rise" style={cardStyle}>
      {accent && (
        <span
          aria-hidden
          className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ background: `linear-gradient(90deg, ${COLORS.accentPrimary}, #E0BE73)` }}
        />
      )}
      <p className="text-[11px] font-medium uppercase tracking-wider mb-1.5" style={{ color: COLORS.textTertiary }}>
        {label}
      </p>
      <p
        className="text-[26px] leading-none tabular-nums"
        style={{ color: valueColor, fontFamily: HEADING, fontWeight: 500, letterSpacing: '-0.01em' }}
      >
        {value}
      </p>
      {sub && (
        <p className="text-[11px] mt-1.5" style={{ color: COLORS.textTertiary }}>{sub}</p>
      )}
    </div>
  );
}

// ─── Order card ──────────────────────────────────────────────────────────────
function OrderCard({
  order,
  fmtDate,
  t,
}: {
  order: CustomerOrderHistory;
  fmtDate: (x: string | null | undefined) => string;
  t: TFunc;
}) {
  const tone = ameexTone(order.ameex.status);
  const destinations =
    order.destinations.length > 0
      ? order.destinations.map((d) => (d.year ? `${d.name} (${d.year})` : d.name)).join(', ')
      : '—';

  return (
    <Link
      to={`/admin/orders/${order.shopifyOrderId}`}
      className="block rounded-xl border p-5 adm-rise transition-shadow hover:shadow-md"
      style={cardStyle}
    >
      {/* Top row: name + date + price */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
              {order.orderName || order.shopifyOrderId}
            </span>
            {order.bundle && (
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                style={{ background: COLORS.accentSoft, color: COLORS.accentHover }}
              >
                {order.bundle}
              </span>
            )}
          </div>
          <p className="text-[12px] mt-0.5" style={{ color: COLORS.textTertiary }}>{fmtDate(order.createdAt)}</p>
        </div>
        <span
          className="tabular-nums"
          style={{ fontFamily: HEADING, fontWeight: 500, fontSize: 20, color: COLORS.textPrimary }}
        >
          {mad(order.totalPrice)}
        </span>
      </div>

      {/* Detail grid */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
        <Field label={t('Destination(s)')} value={destinations} />
        <Field
          label={t('Pages')}
          value={
            order.pageCount != null
              ? `${order.pageCount}p${order.extraPages > 0 ? ` +${order.extraPages} ${t('pages')}` : ''}`
              : '—'
          }
        />
        <Field label={t('Coupon')} value="—" note={t('suivi prochainement')} />
        <Field
          label={t('Livraison estimée')}
          value="—"
        />
      </div>

      {/* Delivery (AMEEX) */}
      <div className="mt-4 pt-4 border-t" style={{ borderColor: COLORS.borderSubtle }}>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${tone.cls ?? ''}`}
            style={tone.cls ? undefined : { background: tone.bg, color: tone.text }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} />
            {t(tone.label)}
          </span>
          {order.ameex.parcelCode && (
            <span className="text-[11px] font-mono" style={{ color: COLORS.textSecondary }}>
              {order.ameex.parcelCode}
            </span>
          )}
          {order.ameex.lastEventAt && (
            <span className="text-[11px] ml-auto" style={{ color: COLORS.textTertiary }}>
              {fmtDate(order.ameex.lastEventAt)}
            </span>
          )}
        </div>
        {order.ameex.statusComment && (
          <p className="text-[12px] mt-1.5" style={{ color: COLORS.textSecondary }}>{order.ameex.statusComment}</p>
        )}
      </div>

      {/* Per-order economics — subtle */}
      <div className="mt-3 flex items-center gap-4 text-[11px]" style={{ color: COLORS.textTertiary }}>
        <span>
          {t('Contribution')}:{' '}
          <span className="font-medium tabular-nums" style={{ color: moneyColor(order.contribution) }}>
            {mad(order.contribution)}
          </span>
        </span>
        <span>
          {t('Coût pub')}:{' '}
          <span className="font-medium tabular-nums" style={{ color: COLORS.textSecondary }}>
            {mad(order.adCost)}
          </span>
        </span>
        <span className="ml-auto font-semibold" style={{ color: COLORS.accentHover }}>
          {t('Voir la commande')} →
        </span>
      </div>
    </Link>
  );
}

function Field({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.textTertiary }}>{label}</p>
      <p className="text-[13px] truncate" style={{ color: COLORS.textPrimary }} title={value}>{value}</p>
      {note && <p className="text-[10px]" style={{ color: COLORS.textTertiary }}>{note}</p>}
    </div>
  );
}
